import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import HistoryIcon from '@/assets/icons/activity-menu-item.svg?react'
import CallsIcon from '@/assets/icons/calls-menu-item.svg?react'
import DashboardIcon from '@/assets/icons/dashboard-menu-item.svg?react'
import DevicesIcon from '@/assets/icons/devices-menu-item.svg?react'
import LeaderboardIcon from '@/assets/icons/leaderboard-menu-item.svg?react'
import LogOutIcon from '@/assets/icons/logout-menu-item.svg?react'
import MissionsIcon from '@/assets/icons/missions-menu-item.svg?react'
import ProfileIcon from '@/assets/icons/profile-menu-item.svg?react'
import ReferralsIcon from '@/assets/icons/referrals-menu-item.svg?react'
import StakeIcon from '@/assets/icons/stake-menu-item.svg?react'
import WalletIcon from '@/assets/icons/wallet-menu-item.svg?react'
import { useSessionProvider } from '@/features/authorization/components/SessionProvider'
import { useIsBuzzEnabled } from '@/features/buzz/api'
import { FormSelectMobileDrawer } from '@/features/missions/components/FormSelectMobileDrawer'
import { useFetchUserWallet } from '@/features/stake-sol/api'
import { ConnectWalletButton } from '@/features/stake-sol/components/ConnectWalletButton'
import { SUPPORTED_LOCALES } from '@/i18n'
import { routes } from '@/pages'
import { Link as LinkButton } from '@/ui-kit/components/Link'
import { useLocale } from '@/ui-kit/providers'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { useOnWindowResize } from '@/utils/useOnWindowResize'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { clsx } from 'clsx'
import { UserIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

const StakeSummaryMobile = () => {
  const { data: userWallet } = useFetchUserWallet()
  const stakeLamports = userWallet?.wallet?.stakeLamports

  return (
    <div className="max-md:flex max-md:gap-4">
      <div className="text-gray-90 mr-4 flex flex-col items-start max-md:whitespace-nowrap min-[600px]:hidden">
        <div className="text-body-3">
          {' '}
          <Trans>You have staked:</Trans>
        </div>
        <div className="text-gray-70 font-semibold">
          {Number(((stakeLamports || 0) / LAMPORTS_PER_SOL).toFixed(2))} SOL
        </div>
      </div>
      <ConnectWalletButton className="mr-2 w-full min-[600px]:hidden" />
    </div>
  )
}

const ProfileAndLogout = ({
  pathname,
  loginUrl,
  onCloseMenu
}: {
  pathname: string
  loginUrl: string
  onCloseMenu: () => void
}) => {
  const [jwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')
  const { logout } = useSessionProvider()

  return jwt ? (
    <>
      <Link
        to="/profile"
        className={clsx(
          'flex w-full gap-2 rounded-lg px-1.5 py-2.5 transition-colors duration-200',
          (pathname === '/profile' || pathname.includes('/profile')) && 'text-orange bg-black-to-white',
          'hover:bg-black-to-white cursor-pointer'
        )}
        onClick={onCloseMenu}
      >
        <ProfileIcon className="h-6 w-6" />
        <Trans>Profile</Trans>
      </Link>

      <button
        type="button"
        className="hover:bg-black-to-white hover:text-orange flex w-full cursor-pointer gap-2 rounded-lg px-1.5 py-2.5 transition-colors duration-200"
        onClick={logout}
      >
        <LogOutIcon className="h-6 w-6" />
        <Trans>Logout</Trans>
      </button>
    </>
  ) : (
    <LinkButton href={loginUrl} size="small">
      <div className="flex items-center gap-2">
        <UserIcon className="h-6 w-6" />
        <Trans>Sign In</Trans>
      </div>
    </LinkButton>
  )
}

const MobileLanguageSwitcher = () => {
  const { t } = useLingui()
  const { locale, setLocale, availableLocales } = useLocale()

  const options = useMemo(
    () =>
      availableLocales.map((code) => {
        const meta = SUPPORTED_LOCALES[code]
        const label = meta?.label ?? code
        return {
          value: code,
          label: meta?.flag ? `${meta.flag} ${label}` : label
        }
      }),
    [availableLocales]
  )

  if (availableLocales.length < 2) return null

  const current = SUPPORTED_LOCALES[locale]

  return (
    <FormSelectMobileDrawer
      buttonClassName="border-none! pl-2 -mb-2 min-mobile:hidden"
      value={locale}
      onChange={setLocale}
      label={t`Language`}
      options={options}
      placeholder={current?.label ?? locale}
      valueLabel={current ? `${current.flag ? `${current.flag} ` : ''}${current.label}` : undefined}
    />
  )
}

/**
 * Names a remote-config flag that gates a nav entry. Resolving it to a
 * `Record<NavFlag, boolean>` in the component makes "named a flag, forgot to resolve
 * it" a compile error.
 */
type NavFlag = 'buzz'

type NavigationItem = {
  value: string
  title: MessageDescriptor
  icon: React.ReactNode
  /** Absent = always shown. Present = shown only when the named flag resolves true. */
  flag?: NavFlag
}

const NAVIGATION_ITEMS: NavigationItem[] = [
  { value: '/calls', title: msg`Calls`, icon: <CallsIcon className="h-6 w-6" />, flag: 'buzz' },
  { value: '/missions', title: msg`Missions`, icon: <MissionsIcon className="h-6 w-6" /> },
  { value: '/dashboard', title: msg`Dashboard`, icon: <DashboardIcon className="h-6 w-6" /> },
  { value: '/leaderboard', title: msg`Leaderboard`, icon: <LeaderboardIcon className="h-6 w-6" /> },
  { value: '/devices', title: msg`Devices`, icon: <DevicesIcon className="h-6 w-6" /> },
  { value: '/referrals', title: msg`Referrals`, icon: <ReferralsIcon className="h-6 w-6" /> },
  { value: '/wallet', title: msg`Wallet`, icon: <WalletIcon className="h-6 w-6" /> },
  { value: '/activity', title: msg`Activity`, icon: <HistoryIcon className="h-6 w-6" /> },
  { value: '/stake', title: msg`Stake SOL`, icon: <StakeIcon className="h-6 w-6" /> }
]

export const NavigationSidebar = ({
  isMenuOpened,
  setIsMenuOpened
}: {
  isMenuOpened: boolean
  setIsMenuOpened: (value: boolean | ((value: boolean) => boolean)) => void
}) => {
  const { t } = useLingui()
  const [jwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')
  const { pathname, search } = useLocation()

  // Calls is the first conditional entry in this nav. The predicate cannot live on the
  // item itself — `NAVIGATION_ITEMS` is a module-level const and the rules of hooks put
  // `useIsBuzzEnabled()` here, in the component. So the item names its flag and the
  // component resolves every name exactly once, before the map.
  const flags: Record<NavFlag, boolean> = { buzz: useIsBuzzEnabled() }
  const navigationItems = NAVIGATION_ITEMS.filter((item) => item.flag === undefined || flags[item.flag])

  useEffect(() => {
    if (pathname) {
      setTimeout(() => window.scrollTo(0, 0), 100)
    }
  }, [pathname])

  useOnWindowResize(() => {
    setIsMenuOpened(false)
  }, 100)

  const returnTo = `${pathname}${search}`
  const loginUrl = `${routes.login.path}?redirect=${encodeURIComponent(returnTo)}`

  return (
    <nav
      className={clsx(
        !isMenuOpened && 'max-mobile:hidden',
        isMenuOpened && 'fixed inset-0 border-0 px-5 pt-20',
        'pt-6',
        'bg-opacity-50 top-0 border-r border-solid border-gray-100 bg-gray-100/30 px-4',
        'max-mobile:top-(--header-height) max-mobile:z-2 max-mobile:bg-(--colorBackground) max-mobile:pt-0'
      )}
    >
      <div className="sticky top-[calc(var(--header-height)+24px)] flex h-[calc(100svh-var(--header-height)-48px)] min-w-[182px] flex-col justify-between">
        <div className="max-mobile:max-h-[calc(100svh-350px)] max-mobile:overflow-y-auto flex flex-col gap-1">
          {navigationItems.map((item) => {
            const isDisabled = !jwt && item.value !== '/missions' && item.value !== '/calls'
            return (
              <Link
                to={isDisabled ? loginUrl : item.value}
                key={item.value}
                className={clsx(
                  'flex w-full gap-2 rounded-lg px-1.5 py-2.5 transition-colors duration-200',
                  (item.value === '/' ? pathname === '/' : pathname.includes(item.value)) &&
                    'text-orange bg-black-to-white',
                  !isDisabled && 'hover:bg-black-to-white cursor-pointer',
                  isDisabled && 'cursor-default opacity-50'
                )}
                onClick={() => {
                  setIsMenuOpened(false)
                }}
              >
                {item.icon}
                {t(item.title)}
              </Link>
            )
          })}
        </div>
        <div className="flex flex-col justify-between gap-4">
          {jwt && <StakeSummaryMobile />}
          <MobileLanguageSwitcher />
          <ProfileAndLogout pathname={pathname} loginUrl={loginUrl} onCloseMenu={() => setIsMenuOpened(false)} />
        </div>
      </div>
    </nav>
  )
}
